import { ColocaAvatar } from "@/shared/components/data-display/ColocaAvatar";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";

import { UserType } from "../types/create-user.types";
import type { UserListItem } from "../types/users-api.types";

export interface UserIdentityCellProps {
  user: UserListItem;
  isCurrentUser: boolean;
}

/**
 * Who this is: avatar, name, email, and at most two markers.
 *
 * The email is mono because that is this design's rule for machine identity — a value you may need to copy or
 * compare character by character. A person's name never is.
 *
 * Nothing about sign-in state belongs here; that is the "Acceso a la cuenta" column's job, and duplicating it
 * as a badge would split one answer across two places.
 */
export function UserIdentityCell({ user, isCurrentUser }: UserIdentityCellProps) {
  const { t } = useTranslations("users");

  return (
    <div className="flex min-w-0 items-center gap-3">
      <ColocaAvatar name={user.full_name} src={user.profile_image ?? undefined} size="sm" />
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-medium text-foreground">{user.full_name}</span>
          {isCurrentUser && (
            <ColocaBadge variant="default" className="shrink-0 text-[10px]">
              {t("badge_you")}
            </ColocaBadge>
          )}
          {user.user_type === UserType.INTERNAL && (
            <ColocaBadge variant="secondary" className="shrink-0 text-[10px]">
              {t("badge_coloca")}
            </ColocaBadge>
          )}
        </div>
        <span className="truncate font-geist-mono text-xs text-muted-foreground">{user.email}</span>
      </div>
    </div>
  );
}
